import React, { Fragment, startTransition, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { get, isEqual, isUndefined } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildMmyPubStatePath, buildVehiclePath, VehicleModel } from 'client/data/models/vehicle-v2';
import { InventoryVehicleEntities } from 'client/data/models/inventory-vehicle';
import { buildClosestDealersPath, DealershipsModel } from 'client/data/models/dealerships';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import {
  PRICE_CHECKER_CREATIVE_ID,
  PRICE_CHECKER_EVENTS,
  PriceCheckerEntities,
  VEHICLE_SELECT_MODE_MAP,
} from 'site-modules/shared/constants/price-checker';
import { DEALERLESS_OEM, DEALERLESS_OEM_TRIAL } from 'site-modules/shared/constants/oem';
import { isUsedState } from 'site-modules/shared/utils/publication-states';
import { vdpLinkBuilder } from 'site-modules/shared/utils/vdp-link-constructor';
import { getModelIncentivesLink } from 'site-modules/shared/utils/incentives-link-constructor';
import { Link } from 'site-modules/shared/components/link/link';
import { StyledSelect } from 'site-modules/shared/components/styled-select/styled-select';
import { PricesForm } from 'site-modules/price-checker/components/prices-form/prices-form';
import { PriceCheckerChart } from 'site-modules/shared/components/price-checker/price-checker-chart/price-checker-chart';
import { PriceCheckerInfo } from 'site-modules/price-checker/components/price-checker-info/price-checker-info';
import { PriceCheckerTaxesFees } from 'site-modules/price-checker/components/price-checker-taxes-fees/price-checker-taxes-fees';
import { GoToAppraiseModal } from 'site-modules/price-checker/components/go-to-appraise-modal/go-to-appraise-modal';
import { VinInput } from 'site-modules/shared/components/price-checker/vin-price-checker/vin-input';
import { submitDeal as submitShareDeal } from 'site-modules/shared/utils/price-checker/community-share-utils';
import { ModeSelect } from 'site-modules/price-checker/components/mode-select/mode-select';
import { useDebounce } from 'site-modules/shared/hooks/use-debounce';

import './vin-price-checker.scss';

function VinPriceCheckerUI({
  vinParams,
  vinStyleTmvDealData = null,
  updateVinParams,
  initialPrices = null,
  zipCode = null,
  isTestDeal = false,
  pageName = null,
  vehicleData = null,
  sessionId = null,
  showCalculators = false,
  isUsedMmy = false,
  isMobile = false,
  sharedDeal = null,
  resetSharedDealParams = null,
  onModeChange,
  closestDealers = null,
  isCore6830International = false,
}) {
  const [showStyleSelect, setShowStyleSelect] = useState(false);
  const [showAppraiseError, setShowAppraiseError] = useState(false);
  const [isUsedVin, setUsedVin] = useState(false);
  const [isGoToAppraiseModalOpen, setGoToAppraiseModalOpen] = useState(true);
  const [isDealRated, setDealRated] = useState(false);
  const [showTaxesFees, setShowTaxesFees] = useState(false);
  const [calculateTaxesFees, setCalculateTaxesFees] = useState(false);
  const [chartPrices, setChartPrices] = useState({
    userDealerPrice: get(sharedDeal, 'userDealerPrice', get(initialPrices, 'userDealerPrice')) || 0,
    userMsrp: get(sharedDeal, 'userMsrp', get(initialPrices, 'userMsrp')) || 0,
  });
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [ratedDealType, setRatedDealType] = useState('');
  const [isDealSubmitted, setDealSubmitted] = useState(false);
  const [hasPriceErrors, setHasPriceErrors] = useState(false);

  const pricesFormRef = React.useRef();
  const priceChartRef = React.useRef();

  const vinStyleId = get(vinStyleTmvDealData, 'vinStyleId');
  const mileage = get(vinStyleTmvDealData, 'mileage');
  const isVinData = get(vinStyleTmvDealData, 'isVinData');
  const hasVinMsrp = !!get(vinStyleTmvDealData, 'msrp');
  const tmvDealTypeData = get(vinStyleTmvDealData, 'tmvDealTypeData');
  const tmvAdjustment = isCore6830International ? undefined : get(vinStyleTmvDealData, 'tmvAdjustment');
  const msrp = get(vinStyleTmvDealData, 'msrp');
  const vinData = get(vinStyleTmvDealData, 'vinData');
  const currentStyle = vinParams.styles && vinParams.styles.find(({ id }) => (vinStyleId || vinParams.styleId) === id);

  const appraisalLink = `/appraisal/?vin=${vinParams.vin}`;
  const isNoPrice = vinStyleId && !tmvDealTypeData;
  const sharedWithCommunityProps = {
    selectedDealer,
    isDealRated,
    closestDealers,
    ratedDealType,
    chartPrices,
    vehicle: { ...vinParams, style: currentStyle },
    handlePriceErrors: setHasPriceErrors,
    isDealSubmitted,
  };

  useEffect(() => {
    if (!vinStyleTmvDealData || chartPrices.userMsrp) {
      return;
    }

    const isUsed = vinStyleTmvDealData.isUsedVin || isUsedMmy;

    startTransition(() => {
      setShowStyleSelect(
        !isUsed && !!(!isVinData && msrp && tmvDealTypeData) && get(vinParams, 'styles.length', 1) !== 1
      );
      setShowAppraiseError(isUsed);
      setUsedVin(isUsed);
      setChartPrices(previousChartPrices => ({
        ...previousChartPrices,
        userMsrp: isVinData ? msrp : 0,
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vinStyleTmvDealData]);

  useEffect(() => {
    if (mileage && isUsedVin) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.ACTION_WIDGET_VIEW,
        event_data: {
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          action_name: TrackingConstant.ACTION_WIDGET_VIEW,
          subaction_name: 'price_checker_used_vin',
          action_cause: TrackingConstant.SYSTEM_ACTION_CAUSE,
          creative_id: PRICE_CHECKER_CREATIVE_ID,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mileage]);

  useEffect(() => {
    if (showStyleSelect) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          action_name: TrackingConstant.ACTION_SHOW_CONTENT,
          subaction_name: 'vin_style_error',
          creative_id: PRICE_CHECKER_CREATIVE_ID,
          value: get(vinParams, 'styles.length'),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showStyleSelect]);

  const resetPricesForm = useCallback(withStyleSelect => {
    pricesFormRef.current.reset();
    setDealRated(false);
    setShowTaxesFees(false);
    setCalculateTaxesFees(false);
    setChartPrices({
      userDealerPrice: 0,
      userMsrp: 0,
    });
    setShowAppraiseError(false);
    setGoToAppraiseModalOpen(true);
    setShowStyleSelect(withStyleSelect);
    setSelectedDealer(null);
    setDealSubmitted(false);
  }, []);

  const setCurrentStyle = useCallback(
    ({ id }) => {
      updateVinParams({ styleId: id });
      resetPricesForm(true);
    },
    [resetPricesForm, updateVinParams]
  );

  const setUserDealerPrice = useCallback(
    userDealerPrice => {
      if (!!sharedDeal && !!resetSharedDealParams) {
        resetSharedDealParams();
      }

      setCalculateTaxesFees(false);
      startTransition(() =>
        setChartPrices({
          ...chartPrices,
          userDealerPrice,
        })
      );
    },
    [chartPrices, resetSharedDealParams, sharedDeal]
  );

  const onChangeDealer = useCallback(newDealer => {
    setSelectedDealer(newDealer);
    setDealSubmitted(false);
  }, []);

  const toggleGoToAppraiseModal = useCallback(() => {
    setGoToAppraiseModalOpen(!isGoToAppraiseModalOpen);
  }, [isGoToAppraiseModalOpen]);

  const switchToTrimMode = useCallback(() => {
    onModeChange({ target: { dataset: { value: VEHICLE_SELECT_MODE_MAP.MMY } } });
  }, [onModeChange]);

  const submitDeal = useCallback(
    chartData => {
      const dealerId = get(selectedDealer, 'id');

      if (dealerId !== 1) {
        EventToolbox.fireCustomEvent(PRICE_CHECKER_EVENTS.LIST_VIEW, {
          additionalData: vehicleData,
          vehicle: { ...vinParams, ...vinData, style: currentStyle },
          prices: chartPrices,
          ratedDealType: get(chartData, 'dealerPrice.label', '') || ratedDealType,
          selectedDealer,
        });

        EventToolbox.fireCustomEvent(PRICE_CHECKER_EVENTS.SUBMIT, {
          subactionName: 'attempt_submit',
        });

        submitShareDeal({
          vehicle: { ...vinParams, style: currentStyle, msrp: get(chartPrices, 'userMsrp') },
          deal: {
            dealType: get(chartData, 'dealerPrice.label', '').toLowerCase() || ratedDealType,
            dealerPrice: get(chartPrices, 'userDealerPrice'),
            zipCode,
            pageName,
          },
          dealer: selectedDealer,
          isTestDeal,
          sessionId,
        });

        setDealSubmitted(true);
      }
    },
    [
      chartPrices,
      currentStyle,
      isTestDeal,
      pageName,
      ratedDealType,
      selectedDealer,
      sessionId,
      vehicleData,
      vinData,
      vinParams,
      zipCode,
    ]
  );

  const updateChartPrices = useCallback(
    userChartPrices => {
      if (showAppraiseError) {
        toggleGoToAppraiseModal();
        return;
      }

      if (!isDealSubmitted && isEqual(chartPrices, userChartPrices)) {
        submitDeal();
      } else {
        setCalculateTaxesFees(false);
        startTransition(() => setChartPrices(userChartPrices));
      }
    },
    [chartPrices, isDealSubmitted, showAppraiseError, submitDeal, toggleGoToAppraiseModal]
  );

  const allowCalculateTaxesFees = useDebounce(() => setCalculateTaxesFees(true), 2000);

  const rateDeal = useCallback(
    ({ tmvChartData }) => {
      startTransition(() => {
        setDealRated(true);
        setShowTaxesFees(true);
        setRatedDealType(get(tmvChartData, 'dealerPrice.label', ''));
      });
      allowCalculateTaxesFees();

      const canScroll = isMobile && !!priceChartRef.current && !sharedDeal;

      if (canScroll) {
        setTimeout(() => priceChartRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }

      if (!!selectedDealer && !hasPriceErrors && !isDealSubmitted) {
        submitDeal(tmvChartData);
      }
    },
    [allowCalculateTaxesFees, hasPriceErrors, isDealSubmitted, isMobile, selectedDealer, sharedDeal, submitDeal]
  );

  return (
    <Fragment>
      <Row className="vin-price-checker pb-2">
        <Col xs={12} md={5} className="pe-lg-1">
          <ModeSelect onModeChange={onModeChange} activeMode={VEHICLE_SELECT_MODE_MAP.VIN} />
          <VinInput
            updateVinParams={updateVinParams}
            onInputChange={resetPricesForm}
            defaultVin={get(sharedDeal, 'vin') || vinParams.vin}
          />
          {!!vinParams.makeName && (
            <div className="mt-0_5 small text-gray-darker">
              {vinParams.year} {vinParams.makeName} {vinParams.modelName}
            </div>
          )}
          {!!(currentStyle && (isVinData || !showStyleSelect)) && (
            <div className="small text-gray-darker">{currentStyle.name}</div>
          )}
          {!!(isUsedVin && mileage) && (
            <div className="small text-gray-darker">
              Mileage: {mileage.toLocaleString('en-US')}{' '}
              <Link
                to={vdpLinkBuilder({
                  make: vinParams.makeSlug,
                  model: vinParams.modelSlug,
                  year: vinParams.year,
                  vin: vinParams.vin,
                })}
                className="text-primary-darker text-underline"
                data-tracking-id="view_vehicle_details"
              >
                See vehicle details
              </Link>
            </div>
          )}
          {isNoPrice && (
            <div className="text-danger small mt-0_5">
              Sorry, we don’t have pricing for this vehicle.{' '}
              {isUsedVin ? (
                <Link to={appraisalLink} className="text-primary" data-tracking-id="appraisal_link_tool_select">
                  Go to Edmunds used car appraiser
                </Link>
              ) : (
                <Fragment>
                  Please use our{' '}
                  <Button
                    className="trim-price-checker-btn align-baseline text-capitalize border-0 p-0 text-primary"
                    size="sm"
                    color="link"
                    onClick={switchToTrimMode}
                  >
                    Edmunds trim Price Checker
                  </Button>
                </Fragment>
              )}
            </div>
          )}
          {showStyleSelect && (
            <StyledSelect
              className="mt-0_25"
              options={vinParams.styles}
              onChange={setCurrentStyle}
              name="style-select"
              value={get(currentStyle, 'slug')}
              valueKey="slug"
              labelKey="name"
              labelText="We can't determine the style of this vehicle. Please select one:"
              labelClassName="text-danger label mt-0_5 mb-0_25"
              data-tracking-id="pc_vin_error_style_select"
            />
          )}
          {showAppraiseError && (
            <Fragment>
              <div className="text-danger label mt-0_5">
                Sorry, we don’t have info on this vehicle - please use our{' '}
                <Link
                  to={appraisalLink}
                  className="text-primary-darker text-underline"
                  data-tracking-id="appraisal_link_tool_select"
                >
                  appraiser
                </Link>
              </div>
              <GoToAppraiseModal
                isOpen={isGoToAppraiseModalOpen}
                toggle={toggleGoToAppraiseModal}
                appraisalLink={appraisalLink}
              />
            </Fragment>
          )}
          <PricesForm
            className="mt-1_5"
            chartPrices={chartPrices}
            setChartPrices={updateChartPrices}
            innerRef={pricesFormRef}
            readOnlyMsrp={!!get(vinStyleTmvDealData, 'isVinData') && hasVinMsrp}
            defaultMsrp={Number(chartPrices.userMsrp)}
            isDealRated={isDealRated}
            disabled={!vinStyleTmvDealData || isNoPrice}
            isMobile={isMobile}
            resetSharedDealParams={resetSharedDealParams}
            mode={VEHICLE_SELECT_MODE_MAP.VIN}
            onChangeDealer={onChangeDealer}
            sharedWithCommunityProps={sharedWithCommunityProps}
            isCore6830International={isCore6830International}
          />
        </Col>
        <Col xs={12} md={7} className="pt-1_5 pt-md-0 ps-lg-1">
          {(DEALERLESS_OEM.includes(vinParams.makeSlug) || DEALERLESS_OEM_TRIAL.includes(vinParams.makeSlug)) &&
          chartPrices.userDealerPrice ? (
            <PriceCheckerInfo
              makeName={vinParams.makeName}
              modelName={vinParams.modelName}
              year={vinParams.year}
              userMsrp={chartPrices.userMsrp}
            />
          ) : (
            <Fragment>
              <PriceCheckerChart
                {...chartPrices}
                styleParams={{
                  styleId: get(currentStyle, 'id'),
                  styleName: get(currentStyle, 'name'),
                  makeName: vinParams.makeName,
                  modelName: vinParams.modelName,
                }}
                bestDealPrice={get(vinStyleTmvDealData, 'bestDealPrice')}
                tmvData={tmvDealTypeData}
                tmvAdjustment={tmvAdjustment}
                setDealRated={rateDeal}
                setUserDealerPrice={setUserDealerPrice}
                isMobile={isMobile}
                sharedDeal={sharedDeal}
                mode={VEHICLE_SELECT_MODE_MAP.VIN}
                priceChartRef={priceChartRef}
                isCore6830International={isCore6830International}
              />

              <PriceCheckerTaxesFees
                styleId={get(currentStyle, 'id')}
                bestDealPrice={get(vinStyleTmvDealData, 'bestDealPrice')}
                tmvData={tmvDealTypeData}
                tmvAdjustment={tmvAdjustment}
                showTaxesFees={showTaxesFees}
                calculateTaxesFees={calculateTaxesFees}
                isDealRated={isDealRated}
                sharedDeal={sharedDeal}
                calculatorProps={{ vin: vinParams.vin }}
                isMobile={isMobile}
                showCalculators={showCalculators}
                incentivesLink={
                  isUsedVin
                    ? null
                    : getModelIncentivesLink(
                        vinParams.makeSlug,
                        vinParams.modelSlug,
                        vinParams.year,
                        get(currentStyle, 'id')
                      )
                }
                mode={VEHICLE_SELECT_MODE_MAP.VIN}
                {...chartPrices}
                isCore6830International={isCore6830International}
              />
            </Fragment>
          )}
        </Col>
      </Row>
    </Fragment>
  );
}

VinPriceCheckerUI.propTypes = {
  onModeChange: PropTypes.func.isRequired,
  updateVinParams: PropTypes.func.isRequired,
  vinParams: PropTypes.shape({
    vin: PropTypes.string,
    makeName: PropTypes.string,
    makeSlug: PropTypes.string,
    modelName: PropTypes.string,
    modelSlug: PropTypes.string,
    styles: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        trim: PropTypes.string,
        submodelName: PropTypes.string,
      })
    ),
    styleId: PropTypes.number,
    year: PropTypes.number,
  }).isRequired,
  initialPrices: PropTypes.shape({
    userDealerPrice: PropTypes.number,
    userMsrp: PropTypes.number,
  }),
  vinStyleTmvDealData: InventoryVehicleEntities.VinStyleTmvDealData,
  isUsedMmy: PropTypes.bool,
  showCalculators: PropTypes.bool,
  isMobile: PropTypes.bool,
  zipCode: PropTypes.string,
  sharedDeal: PriceCheckerEntities.SharedDataEntity,
  resetSharedDealParams: PropTypes.func,
  visitorId: PropTypes.string,
  closestDealers: PropTypes.arrayOf(PropTypes.shape({})),
  isTestDeal: PropTypes.bool,
  pageName: PropTypes.string,
  vehicleData: PropTypes.shape({
    id: PropTypes.number,
    modelYearId: PropTypes.number,
    make: PropTypes.shape({
      id: PropTypes.number,
    }),
    model: PropTypes.shape({
      modelLinkCode: PropTypes.string,
    }),
    submodels: PropTypes.shape({
      slug: PropTypes.string,
    }),
  }),
  sessionId: PropTypes.string,
  isCore6830International: PropTypes.bool,
};

export const VinPriceChecker = connectToModel(VinPriceCheckerUI, {
  isUsedMmy: bindToPath(
    ({ vinParams: { vin, makeSlug, modelSlug, year }, vinStyleTmvDealData }) =>
      vin &&
      isUndefined(get(vinStyleTmvDealData, 'isUsedVin')) &&
      buildMmyPubStatePath({ make: makeSlug, model: modelSlug, year }),
    VehicleModel,
    isUsedState
  ),
  closestDealers: bindToPath(
    ({ vinParams: { makeSlug: make } }) => !!make && buildClosestDealersPath({ make }),
    DealershipsModel
  ),
  vehicleData: bindToPath(
    ({ vinParams: { makeSlug, modelSlug, year } }) =>
      makeSlug && modelSlug && year && buildVehiclePath({ make: makeSlug, model: modelSlug, year }),
    VehicleModel
  ),
});
